/**
 * HTTP环境下的图片缓存方案
 * 使用内存缓存和localStorage实现图片缓存
 */

class ImageCacheHTTP {
  constructor() {
    this.cache = new Map();
    this.loadingPromises = new Map();
    this.maxCacheSize = 500; // 最大缓存数量
    this.storageKey = 'image-cache-urls';
    
    // 从localStorage恢复缓存URL列表
    this.restoreCacheFromStorage();
  }

  /**
   * 从localStorage恢复缓存
   */
  restoreCacheFromStorage() {
    try {
      const cachedUrls = JSON.parse(localStorage.getItem(this.storageKey) || '[]');
      cachedUrls.forEach(url => {
        // 只恢复URL，不恢复实际图片数据（节省内存）
        this.cache.set(url, { url, cached: true });
      });
      console.log(`从localStorage恢复了 ${cachedUrls.length} 个缓存URL`);
    } catch (error) {
      console.warn('恢复缓存失败:', error);
    }
  }

  /**
   * 保存缓存URL到localStorage
   */
  saveCacheToStorage() {
    try {
      const cachedUrls = Array.from(this.cache.keys()).slice(-this.maxCacheSize);
      localStorage.setItem(this.storageKey, JSON.stringify(cachedUrls));
    } catch (error) {
      console.warn('保存缓存失败:', error);
    }
  }

  /**
   * 获取缓存的图片
   * @param {string} url 图片URL
   * @returns {Promise<string>} 图片URL
   */
  async getImage(url) {
    // 如果已经在缓存中，直接返回
    if (this.cache.has(url)) {
      const cached = this.cache.get(url);
      if (cached.blobUrl) {
        return cached.blobUrl;
      }
      return url;
    }

    // 如果正在加载，返回加载中的Promise
    if (this.loadingPromises.has(url)) {
      return this.loadingPromises.get(url);
    }

    // 创建加载Promise
    const loadPromise = this.loadImage(url);
    this.loadingPromises.set(url, loadPromise);

    try {
      const result = await loadPromise;
      return result;
    } finally {
      this.loadingPromises.delete(url);
    }
  }

  /**
   * 加载并缓存图片
   * @param {string} url 图片URL
   * @returns {Promise<string>} 图片URL或blob URL
   */
  async loadImage(url) {
    try {
      // 预加载图片
      await this.preloadImage(url);
      
      // 标记为已缓存
      this.cache.set(url, { url, cached: true, timestamp: Date.now() });
      
      // 保存到localStorage
      this.saveCacheToStorage();
      
      console.log('图片已缓存:', url);
      return url;
    } catch (error) {
      console.error('图片加载失败:', url, error);
      return url;
    }
  }

  /**
   * 获取缓存的图片（返回blob URL以避免重复请求）
   * @param {string} url 图片URL
   * @returns {Promise<string>} blob URL或原始URL
   */
  async getImageBlob(url) {
    console.log('请求图片blob:', url);
    
    // 如果已经有blob URL，直接返回
    if (this.cache.has(url)) {
      const cached = this.cache.get(url);
      console.log('找到缓存:', url, 'blob URL:', cached.blobUrl, 'cached:', cached.cached);
      if (cached.blobUrl && cached.cached !== false) {
        return cached.blobUrl;
      }
      // 如果缓存失败，返回原始URL
      return url;
    }

    // 如果正在加载，返回加载中的Promise
    if (this.loadingPromises.has(url)) {
      console.log('图片正在加载中:', url);
      return this.loadingPromises.get(url);
    }

    // 创建加载Promise
    const loadPromise = this.loadImageAsBlob(url);
    this.loadingPromises.set(url, loadPromise);

    try {
      const blobUrl = await loadPromise;
      console.log('图片blob加载完成:', url, blobUrl);
      return blobUrl;
    } finally {
      this.loadingPromises.delete(url);
    }
  }

  /**
   * 将图片加载为blob并返回blob URL
   * @param {string} url 图片URL
   * @returns {Promise<string>} blob URL
   */
  async loadImageAsBlob(url) {
    try {
      console.log('开始加载图片为blob:', url);
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      console.log('图片fetch成功，开始转换为blob:', url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      // 缓存blob URL
      this.cache.set(url, { 
        url, 
        blobUrl, 
        cached: true, 
        timestamp: Date.now() 
      });
      
      // 保存到localStorage
      this.saveCacheToStorage();
      
      console.log('图片已转换为blob:', url, 'blob URL:', blobUrl);
      return blobUrl;
    } catch (error) {
      console.error('图片blob转换失败:', url, error);
      // 即使失败也要缓存URL，避免重复尝试
      this.cache.set(url, { 
        url, 
        cached: false, 
        error: error.message,
        timestamp: Date.now() 
      });
      return url;
    }
  }

  /**
   * 预加载图片
   * @param {string} url 图片URL
   * @returns {Promise<void>}
   */
  preloadImage(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        resolve();
      };
      
      img.onerror = () => {
        reject(new Error('图片加载失败'));
      };
      
      img.src = url;
    });
  }

  /**
   * 批量预加载图片
   * @param {Array} urls 图片URL数组
   * @returns {Promise<void>}
   */
  async preloadImages(urls) {
    console.log(`开始预加载 ${urls.length} 张图片...`);
    
    const promises = urls.map(url => this.getImage(url));
    await Promise.allSettled(promises);
    
    console.log('预加载完成');
  }

  /**
   * 检查图片是否已缓存
   * @param {string} url 图片URL
   * @returns {boolean}
   */
  isCached(url) {
    return this.cache.has(url);
  }

  /**
   * 获取缓存信息
   * @returns {Object} 缓存信息
   */
  getCacheInfo() {
    return {
      size: this.cache.size,
      maxSize: this.maxCacheSize,
      urls: Array.from(this.cache.keys())
    };
  }

  /**
   * 清除缓存
   */
  clearCache() {
    this.cache.clear();
    this.loadingPromises.clear();
    localStorage.removeItem(this.storageKey);
    console.log('缓存已清除');
  }

  /**
   * 清理过期缓存
   */
  cleanupCache() {
    if (this.cache.size <= this.maxCacheSize) {
      return;
    }

    // 按时间排序，删除最旧的缓存
    const entries = Array.from(this.cache.entries());
    entries.sort((a, b) => (a[1].timestamp || 0) - (b[1].timestamp || 0));
    
    const toDelete = entries.slice(0, this.cache.size - this.maxCacheSize);
    toDelete.forEach(([url]) => {
      this.cache.delete(url);
    });

    this.saveCacheToStorage();
    console.log(`清理了 ${toDelete.length} 个过期缓存`);
  }
}

// 创建单例实例
const imageCacheHTTP = new ImageCacheHTTP();

export default imageCacheHTTP;
